import { useState, useEffect } from 'react';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { Textarea } from '@/core/components/ui/textarea';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { ApplicationTypeSelect } from '../atoms/ApplicationTypeSelect';
import { LanguageInput } from '../atoms/LanguageInput';
import {
  TranslationKey,
  Language,
  ApplicationType,
  ApplicationTypeLabels,
  CreateTranslationRequest,
  UpdateTranslationRequest,
  isValidKeyName,
} from '../../types/translation-types';

interface TranslationFormProps {
  translation?: TranslationKey | null;
  languages: Language[];
  onSubmit: (data: CreateTranslationRequest | UpdateTranslationRequest) => Promise<void>;
  isSubmitting: boolean;
  error?: string | null;
  isEditMode: boolean;
  /** Pre-selected application type (from parent filter) */
  applicationType?: ApplicationType | null;
}

interface FormData {
  keyName: string;
  applicationType: ApplicationType;
  description: string;
  values: Record<string, string>;
}

export function TranslationForm({
  translation,
  languages,
  onSubmit,
  isSubmitting,
  error,
  isEditMode,
  applicationType: preSelectedAppType,
}: TranslationFormProps) {
  const [formData, setFormData] = useState<FormData>({
    keyName: '',
    applicationType: ApplicationType.ReactWeb,
    description: '',
    values: {},
  });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Initialize form data when translation changes
  useEffect(() => {
    if (translation) {
      const values: Record<string, string> = {};
      translation.values.forEach((v) => {
        values[v.languageCode] = v.value;
      });
      setFormData({
        keyName: translation.keyName,
        applicationType: translation.applicationType,
        description: translation.description || '',
        values,
      });
    } else {
      // Initialize empty values for all languages
      const values: Record<string, string> = {};
      languages.forEach((lang) => {
        values[lang.languageCode] = '';
      });
      setFormData({
        keyName: '',
        // Use pre-selected app type from parent filter, fallback to ReactWeb
        applicationType: preSelectedAppType ?? ApplicationType.ReactWeb,
        description: '',
        values,
      });
    }
  }, [translation, languages, preSelectedAppType]);

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!isEditMode) {
      if (!formData.keyName.trim()) {
        errors.push('Key name is required');
      } else if (!isValidKeyName(formData.keyName)) {
        errors.push('Key name must start with a letter and contain only lowercase letters, numbers, and underscores');
      }
    }

    // English is required - a whitespace-only value is valid (e.g. a single space separator)
    const englishValue = formData.values['en'];
    if (!englishValue) {
      errors.push('English translation is required');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Keep values exactly as typed - leading/trailing spaces are intentional (same as inline grid edit)
    const values = Object.entries(formData.values)
      .filter(([, value]) => value !== '')
      .map(([languageCode, value]) => ({ languageCode, value }));

    if (isEditMode) {
      const updateData: UpdateTranslationRequest = {
        description: formData.description || undefined,
        values,
      };
      await onSubmit(updateData);
    } else {
      const createData: CreateTranslationRequest = {
        keyName: formData.keyName,
        applicationType: formData.applicationType,
        description: formData.description || undefined,
        values,
      };
      await onSubmit(createData);
    }
  };

  const handleLanguageValueChange = (languageCode: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        [languageCode]: value,
      },
    }));
  };

  // Sort languages: English first, then by sortOrder
  const sortedLanguages = [...languages].sort((a, b) => {
    if (a.languageCode === 'en') return -1;
    if (b.languageCode === 'en') return 1;
    return a.sortOrder - b.sortOrder;
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-6" id="translation-form">
      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc pl-4">
              {validationErrors.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* API Error */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Application Type - show as read-only text since it's pre-selected from the filter */}
      <div className="space-y-1.5">
        <Label>Application Type</Label>
        <div className="px-3 py-2 border rounded-md bg-muted text-sm">
          {ApplicationTypeLabels[formData.applicationType]}
        </div>
      </div>

      {/* Key Name */}
      <div className="space-y-1.5">
        <Label htmlFor="keyName">
          Key Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="keyName"
          value={formData.keyName}
          onChange={(e) => setFormData((prev) => ({ ...prev, keyName: e.target.value.toLowerCase() }))}
          disabled={isEditMode || isSubmitting}
          placeholder="e.g., button_submit, error_network_failed"
          className="font-mono"
        />
        <p className="text-xs text-muted-foreground">
          Lowercase letters, numbers, and underscores only. Must start with a letter.
        </p>
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <Label htmlFor="description">Description (optional)</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
          disabled={isSubmitting}
          placeholder="Describe where and how this translation is used..."
          rows={2}
        />
      </div>

      {/* Language Values */}
      <div className="space-y-4">
        <Label className="text-base font-semibold">Translations</Label>
        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
          {sortedLanguages.map((lang) => (
            <LanguageInput
              key={lang.languageCode}
              languageCode={lang.languageCode}
              languageName={lang.languageName}
              value={formData.values[lang.languageCode] || ''}
              onChange={(value) => handleLanguageValueChange(lang.languageCode, value)}
              required={lang.languageCode === 'en'}
              showWarning={lang.languageCode !== 'en'}
              disabled={isSubmitting}
            />
          ))}
        </div>
      </div>
    </form>
  );
}
